/**
 * Runtime config for the coach dashboard.
 *
 * Today's values are mostly platform-tuning knobs that were
 * previously hardcoded constants (polling cadence, realtime timeout,
 * etc.). Exposing them here gives us three precedence layers, in
 * order of priority:
 *
 *   1. `window.__ARC_COACH_CONFIG__` — server-injected overrides.
 *      The `index.html` shell can `<script>window.__ARC_COACH_CONFIG__
 *      = {...}</script>` before the SPA loads; Cloudflare Workers
 *      can inject this from a KV-backed config.
 *   2. `localStorage["arc.coach.config"]` — per-device dev override.
 *      Useful for one-off tuning at the range without a rebuild.
 *   3. Static defaults baked into this file. Safe for production.
 *
 * Each value flows through `readOverride` which validates the shape
 * + bounds so a bad override never produces a wedged UI.
 */

export interface CoachRuntimeConfig {
  /** How often the inbox badge polls when realtime is unavailable. */
  inboxPollIntervalMs: number;
  /** How long to wait for the realtime channel to subscribe before falling back to polling. */
  inboxRealtimeSubscribeTimeoutMs: number;
}

const DEFAULTS: CoachRuntimeConfig = {
  inboxPollIntervalMs: 45_000,
  inboxRealtimeSubscribeTimeoutMs: 6_000,
};

const BOUNDS: Record<
  keyof CoachRuntimeConfig,
  { min: number; max: number }
> = {
  inboxPollIntervalMs: { min: 5_000, max: 10 * 60_000 },
  inboxRealtimeSubscribeTimeoutMs: { min: 1_000, max: 60_000 },
};

const LOCAL_STORAGE_KEY = "arc.coach.config";

declare global {
  interface Window {
    __ARC_COACH_CONFIG__?: Partial<CoachRuntimeConfig>;
  }
}

let memoized: CoachRuntimeConfig | null = null;

/**
 * Resolve the runtime config with override precedence. Memoized per
 * page load — overrides set after call are ignored until a reload,
 * which keeps the badge interval from oscillating mid-session.
 */
export function getCoachRuntimeConfig(): CoachRuntimeConfig {
  if (memoized) return memoized;
  const serverOverride =
    typeof window !== "undefined" ? window.__ARC_COACH_CONFIG__ ?? {} : {};
  const deviceOverride = readLocalStorageOverride();
  memoized = {
    inboxPollIntervalMs: resolveNumber(
      "inboxPollIntervalMs",
      serverOverride.inboxPollIntervalMs,
      deviceOverride.inboxPollIntervalMs,
    ),
    inboxRealtimeSubscribeTimeoutMs: resolveNumber(
      "inboxRealtimeSubscribeTimeoutMs",
      serverOverride.inboxRealtimeSubscribeTimeoutMs,
      deviceOverride.inboxRealtimeSubscribeTimeoutMs,
    ),
  };
  return memoized;
}

/** Test-only helper; never call from app code. */
export function _resetCoachRuntimeConfigForTesting(): void {
  memoized = null;
}

function readLocalStorageOverride(): Partial<CoachRuntimeConfig> {
  if (typeof window === "undefined" || !window.localStorage) return {};
  try {
    const raw = window.localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return {};
    return parsed as Partial<CoachRuntimeConfig>;
  } catch {
    return {};
  }
}

function resolveNumber(
  key: keyof CoachRuntimeConfig,
  serverOverride: unknown,
  deviceOverride: unknown,
): number {
  const bounds = BOUNDS[key];
  const candidates = [serverOverride, deviceOverride];
  for (const candidate of candidates) {
    if (typeof candidate === "number" && Number.isFinite(candidate)) {
      const clamped = Math.min(Math.max(candidate, bounds.min), bounds.max);
      return clamped;
    }
  }
  return DEFAULTS[key];
}
